import React, { RefObject } from 'react';
import { VideoFeed } from './VideoFeed';
import { CountryFlag } from '../CountryFlag';
import { CountryInfo } from '@/services/geolocation';

type PeerVideoStatus = 'waiting' | 'connecting' | 'reconnecting' | 'connected' | 'disconnected' | 'error';

interface DefaultLayoutProps {
  // Video refs
  localVideoRef: RefObject<HTMLVideoElement>;
  remoteVideoRef: RefObject<HTMLVideoElement>;
  canvasRef: RefObject<HTMLCanvasElement>;

  // Streams and media state
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isVideoEnabled: boolean;

  // Peer state
  sessionActive: boolean;
  peerVideoStatus: PeerVideoStatus;
  rtcConnectionState?: string;
  peerVideoEnabled: boolean;
  isPeerVideoHidden: boolean;
  peerAudioEnabled: boolean;

  // Filters
  selfFilter: string;
  peerFilter: string;

  // Layout settings
  videoSizeRatio: number;
  swapPositions?: boolean;

  // Audio state
  isPeerAudioMuted: boolean;

  // Country flags
  userCountry?: CountryInfo | null;
  peerCountry?: CountryInfo | null;
}

/**
 * Default side-by-side layout component
 * Renders local and remote video feeds with adjustable sizing
 */
export const DefaultLayout: React.FC<DefaultLayoutProps> = ({
  localVideoRef,
  remoteVideoRef,
  canvasRef,
  localStream,
  remoteStream,
  isVideoEnabled,
  sessionActive,
  peerVideoStatus,
  rtcConnectionState,
  peerVideoEnabled,
  isPeerVideoHidden,
  peerAudioEnabled,
  selfFilter,
  peerFilter,
  videoSizeRatio,
  swapPositions = false,
  isPeerAudioMuted,
  userCountry,
  peerCountry
}) => {
  const hideSelf = videoSizeRatio <= 1;
  const hidePeer = videoSizeRatio >= 99;

  // Calculate which video goes where based on swapPositions
  const leftWidth = swapPositions
    ? (hidePeer ? '0%' : (hideSelf ? '100%' : `${100 - videoSizeRatio}%`))
    : (hideSelf ? '0%' : (hidePeer ? '100%' : `${videoSizeRatio}%`));

  const rightWidth = swapPositions
    ? (hideSelf ? '0%' : (hidePeer ? '100%' : `${videoSizeRatio}%`))
    : (hidePeer ? '0%' : (hideSelf ? '100%' : `${100 - videoSizeRatio}%`));

  const leftVisibility = (swapPositions ? hidePeer : hideSelf) ? 'opacity-0' : 'opacity-100';
  const rightVisibility = (swapPositions ? hideSelf : hidePeer) ? 'opacity-0' : 'opacity-100';

  // Determine which video is on which side
  const leftIsLocal = !swapPositions;
  const rightIsLocal = swapPositions;

  return (
    <div className="flex flex-row h-full w-full bg-black overflow-hidden">
      <canvas ref={canvasRef} className="hidden"/>

      {/* Left container */}
      <div
        className={`flex h-full relative transition-opacity duration-200 ${leftVisibility}`}
        style={{ width: leftWidth }}
      >
        <VideoFeed
          videoRef={leftIsLocal ? localVideoRef : remoteVideoRef}
          videoType={leftIsLocal ? 'local' : 'remote'}
          stream={leftIsLocal ? localStream : remoteStream}
          filter={leftIsLocal ? selfFilter : peerFilter}
          muted={leftIsLocal ? true : isPeerAudioMuted}
          mirror={leftIsLocal}
          showVideo={leftIsLocal
            ? !!(localStream && isVideoEnabled)
            : !!(sessionActive && rtcConnectionState === 'connected' && peerVideoEnabled && !isPeerVideoHidden)
          }
          overlayState={{
            hasStream: leftIsLocal ? !!localStream : !!remoteStream,
            sessionActive,
            peerVideoStatus,
            isVideoEnabled: leftIsLocal ? isVideoEnabled : undefined,
            isPeerVideoHidden: leftIsLocal ? undefined : isPeerVideoHidden,
            peerVideoEnabled: leftIsLocal ? undefined : peerVideoEnabled,
            rtcConnectionState,
            peerAudioEnabled: leftIsLocal ? undefined : peerAudioEnabled,
            swapPositions,
            userCountry,
            peerCountry
          }}
        />
        {/* Country flag for left side */}
        {!leftIsLocal && sessionActive && rtcConnectionState === 'connected' && peerCountry && (
          <div className="absolute bottom-2 left-2">
            <CountryFlag country={peerCountry} size="sm"/>
          </div>
        )}
        {leftIsLocal && sessionActive && rtcConnectionState === 'connected' && userCountry && (
          <div className="absolute bottom-2 left-2">
            <CountryFlag country={userCountry} size="sm"/>
          </div>
        )}
      </div>

      {/* Right container */}
      <div
        className={`flex h-full relative transition-opacity duration-200 ${rightVisibility}`}
        style={{ width: rightWidth }}
      >
        <VideoFeed
          videoRef={rightIsLocal ? localVideoRef : remoteVideoRef}
          videoType={rightIsLocal ? 'local' : 'remote'}
          stream={rightIsLocal ? localStream : remoteStream}
          filter={rightIsLocal ? selfFilter : peerFilter}
          muted={rightIsLocal ? true : isPeerAudioMuted}
          mirror={rightIsLocal}
          showVideo={rightIsLocal
            ? !!(localStream && isVideoEnabled)
            : !!(sessionActive && rtcConnectionState === 'connected' && peerVideoEnabled && !isPeerVideoHidden)
          }
          overlayState={{
            hasStream: rightIsLocal ? !!localStream : !!remoteStream,
            sessionActive,
            peerVideoStatus,
            isVideoEnabled: rightIsLocal ? isVideoEnabled : undefined,
            isPeerVideoHidden: rightIsLocal ? undefined : isPeerVideoHidden,
            peerVideoEnabled: rightIsLocal ? undefined : peerVideoEnabled,
            rtcConnectionState,
            peerAudioEnabled: rightIsLocal ? undefined : peerAudioEnabled,
            swapPositions,
            userCountry,
            peerCountry
          }}
        />
        {/* Country flag for right side */}
        {!rightIsLocal && sessionActive && rtcConnectionState === 'connected' && peerCountry && (
          <div className="absolute bottom-2 left-2">
            <CountryFlag country={peerCountry} size="sm"/>
          </div>
        )}
        {rightIsLocal && sessionActive && rtcConnectionState === 'connected' && userCountry && (
          <div className="absolute bottom-2 left-2">
            <CountryFlag country={userCountry} size="sm"/>
          </div>
        )}
      </div>
    </div>
  );
};
